import React, { useCallback, useMemo, useState } from "react";
import styles from "./UsageView.module.scss";
import ChevronDown from "src/assets/icons/dropdown.svg?react";
import ArrowRightIcon from "src/assets/icons/arrow-lesson.svg?react";
import { Collapse } from "@src/utils/componentCasts";
import ReactDataGrid from "@inovua/reactdatagrid-community";
import "../engagementTable.css";
import LessonProgressModal from "@src/components/lesson-progress-modal/LessonProgressModal";
import UnitProgressModal from "@src/components/unit-progress-modal/UnitProgressModal";
import { TypeColumn } from "@inovua/reactdatagrid-community/types";
import { LessonByCourse } from "@src/api/graphql-codegen";
import { Pagination } from "antd";
import ChevronLeft from "../assets/chevron-left.svg?react";
import ChevronRight from "../assets/chevron-right.svg?react";
import { shortenYearFormatDate } from "@src/utils/time";

type Props = {
  gradeId: string;
  grade: LessonByCourse;
  selectedId?: string | null;
  selected?: boolean;
  handleSeleted?: (program: any) => void;
  loading?: boolean;
  isDistrictAdmin: boolean;
  isAussie: boolean;
  isLast?: boolean;
  noData?: boolean;
};

const getRowspan = ({ value, dataSourceArray, rowIndex, column }) => {
  let rowspan = 1;

  const prevData = dataSourceArray[rowIndex - 1];
  if (prevData && prevData[column.name] === value) {
    return rowspan;
  }
  let currentRowIndex = rowIndex + 1;
  while (
    dataSourceArray[currentRowIndex] &&
    dataSourceArray[currentRowIndex][column.name] === value
  ) {
    rowspan++;
    currentRowIndex++;
  }
  return rowspan;
};

const getSameTeacherRowspan = ({
  value,
  dataSourceArray,
  rowIndex,
  column,
}) => {
  let rowspan = 1;

  const prevData = dataSourceArray[rowIndex - 1];
  if (
    prevData &&
    prevData[column.name] === value &&
    dataSourceArray[rowIndex]["teacherName"] ===
      dataSourceArray[rowIndex - 1]["teacherName"]
  ) {
    return rowspan;
  }
  let currentRowIndex = rowIndex + 1;
  while (
    dataSourceArray[currentRowIndex] &&
    dataSourceArray[currentRowIndex][column.name] === value &&
    dataSourceArray[currentRowIndex]["teacherName"] ===
      dataSourceArray[rowIndex]["teacherName"]
  ) {
    rowspan++;
    currentRowIndex++;
  }
  return rowspan;
};

const CustomHeader = ({ title }) => {
  return (
    <div
      className={[
        styles.tableHeaderCenter,
        title !== "Completed Lessons" ? styles.headerMiddle : "",
      ].join(" ")}
    >
      <span>{title}</span>
    </div>
  );
};

const perPage = 10;

function GradeColumnView(props: Props) {
  const {
    gradeId,
    grade,
    selectedId,
    selected,
    loading,
    handleSeleted,
    isDistrictAdmin,
    isAussie,
    isLast = false,
    noData = false,
  } = props;
  const [showLesson, setShowLesson] = useState<any>(undefined);
  const [showUnit, setShowUnit] = useState<any>(undefined);

  const [skip, setSkip] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const isSelected = useMemo(() => {
    return selected && selectedId === gradeId ? true : false;
  }, [selected, selectedId, gradeId]);

  const handleSelectGrade = useCallback(() => {
    handleSeleted?.(grade);
  }, [handleSeleted, grade]);

  const handleShowModal = useCallback((model: any) => {
    if (model?.type === "Unit") {
      setShowUnit(model);
      setShowLesson(undefined);
    } else if (model?.type === "CourseLesson") {
      setShowUnit(undefined);
      setShowLesson(model);
    } else if (model?.type === "Standalone") {
      setShowUnit(undefined);
      setShowLesson(model);
    } else {
      setShowUnit(undefined);
      if (model?.lessonId) {
        setShowLesson(model);
      } else {
        setShowLesson(undefined);
      }
    }
  }, []);

  const dataSource = useMemo(() => {
    let _dataSource: any[] = [];
    grade?.lessons?.forEach((elem: any, index: number) => {
      (elem?.items ?? []).forEach((item: any, sindex: number) => {
        _dataSource.push({
          id: `${elem?.teacherName}_${index}_${sindex}`,
          teacherName: elem?.teacherName ?? "",
          completedLessons: elem?.completedLessons ?? 0,
          schoolName: elem?.schoolName ?? "",
          lessonId: item?.lessonId ?? "",
          lessonName: item?.lessonName ?? "",
          completedDate: item?.completedAt ?? "",
          unitId: item?.unitId ?? "",
          unitName: item?.unitName ?? "",
          type: item?.type ?? "",
          lessonType: item?.lessonType ?? "",
        });
      });
    });
    return _dataSource;
  }, [grade]);

  const tableColumns = useMemo(() => {
    const _tableColumns: TypeColumn[] = isAussie
      ? [
          {
            defaultFlex: 1,
            header: <CustomHeader title="Teacher Name" />,
            name: "teacherName",
            render: ({ value }) => (
              <span className={styles.teachColumn}>{value}</span>
            ),
            resizable: false,
            rowspan: (param) => getRowspan(param),
            sortable: false,
          },
          {
            defaultFlex: 0.8,
            header: <CustomHeader title="Completed Lessons" />,
            name: "completedLessons",
            resizable: false,
            rowspan: (param) => getSameTeacherRowspan(param),
            sortable: false,
            textAlign: "center" as any,
          },
          {
            defaultFlex: 1.8,
            header: <CustomHeader title="Lesson Name" />,
            name: "lessonName",
            render: ({ data, value }) => {
              return (
                <div
                  className={[
                    styles.column,
                    data.lessonId !== "" ? styles.columnHover : "",
                  ].join(" ")}
                  onClick={() => handleShowModal(data)}
                  style={{
                    cursor: data.lessonId !== "" ? "pointer" : "default",
                  }}
                >
                  <div className={styles.inline}>
                    <span className={styles.lessonName}>{value}</span>
                    <div
                      className={styles.iconContainer}
                      onClick={() => {}}
                      role="button"
                    >
                      <ArrowRightIcon className={styles.panelIcon} />
                    </div>
                  </div>
                </div>
              );
            },
            resizable: false,
            sortable: false,
            textAlign: "start" as any,
          },
          {
            defaultFlex: 1.5,
            header: <CustomHeader title="Unit/Standalone" />,
            name: "type",
            render: ({ data, value }) => {
              if (value === "Unit" && data.unitName) {
                return (
                  <div className={styles.column}>
                    <span className={styles.lessonName}>{data.unitName}</span>
                  </div>
                );
              } else if (value === "Standalone") {
                return <div className={styles.column}>Standalone Lesson</div>;
              } else {
                if (data.unitName) {
                  return <div className={styles.column}>{data.unitName}</div>;
                } else if (!data.unitName && data.lessonName) {
                  return <div className={styles.column}>Standalone Lesson</div>;
                } else {
                  return <div className={styles.column}>Standalone Lesson</div>;
                }
              }
            },
            resizable: false,
            sortable: false,
            textAlign: "center" as any,
          },
          {
            defaultFlex: 1,
            header: <CustomHeader title="Lesson Type" />,
            name: "lessonType",
            render: ({ data, value }) => {
              if (data.type === "Unit") {
                return <div className={styles.column}>Multi Lesson Unit</div>;
              } else {
                return <div className={styles.column}>{value}</div>;
              }
            },
            resizable: false,
            sortable: false,
            textAlign: "center" as any,
          },
          {
            defaultFlex: 0.8,
            header: <CustomHeader title="Date Completed" />,
            name: "completedDate",
            render: ({ value }) => (
              <span className={styles.lastColumn}>
                {shortenYearFormatDate(value)}
              </span>
            ),
            resizable: false,
            sortable: false,
            textAlign: "start" as any,
          },
        ]
      : [
          {
            defaultFlex: 1,
            header: <CustomHeader title="Teacher Name" />,
            name: "teacherName",
            render: ({ value }) => (
              <span className={styles.teachColumn}>{value}</span>
            ),
            resizable: false,
            rowspan: (param) => getRowspan(param),
            sortable: false,
          },
          {
            defaultFlex: 0.8,
            header: <CustomHeader title="Completed Lessons" />,
            name: "completedLessons",
            resizable: false,
            rowspan: (param) => getSameTeacherRowspan(param),
            sortable: false,
            textAlign: "center" as any,
          },
          {
            defaultFlex: 2.5,
            header: <CustomHeader title="Lesson Name" />,
            name: "lessonName",
            render: ({ data, value }) => {
              return (
                <div
                  className={[
                    styles.column,
                    data.lessonId !== "" ? styles.columnHover : "",
                  ].join(" ")}
                  onClick={() => handleShowModal(data)}
                  style={{
                    cursor: data.lessonId !== "" ? "pointer" : "default",
                  }}
                >
                  <div className={styles.inline}>
                    <span>{value}</span>
                    <div
                      className={styles.iconContainer}
                      onClick={() => {}}
                      role="button"
                    >
                      <ArrowRightIcon className={styles.panelIcon} />
                    </div>
                  </div>
                </div>
              );
            },
            resizable: false,
            sortable: false,
            textAlign: "start" as any,
          },
          {
            defaultFlex: 1,
            header: <CustomHeader title="Lesson Type" />,
            name: "lessonType",
            render: ({ data, value }) => {
              if (data.type === "Unit") {
                return <div className={styles.column}>Multi Lesson Unit</div>;
              } else {
                return <div className={styles.column}>{value}</div>;
              }
            },
            resizable: false,
            sortable: false,
            textAlign: "center" as any,
          },
          {
            defaultFlex: 1,
            header: <CustomHeader title="Date Completed" />,
            name: "completedDate",
            render: ({ value }) => (
              <span className={styles.lastColumn}>{value}</span>
            ),
            resizable: false,
            sortable: false,
            textAlign: "start" as any,
          },
        ];
    if (isDistrictAdmin) {
      _tableColumns.splice(1, 0, {
        defaultFlex: 1.2,
        header: <CustomHeader title="School Name" />,
        name: "schoolName",
        render: ({ value }) => (
          <span className={styles.teachColumn}>{value}</span>
        ),
        resizable: false,
        rowspan: (param) => getSameTeacherRowspan(param),
        sortable: false,
      });
    }
    return _tableColumns;
  }, [handleShowModal, isAussie, isDistrictAdmin]);

  const handleLimitChange = () => {
    setSkip(skip);
  };

  const handleChangePage = useCallback((page: number) => {
    setCurrentPage(page);
    const newSkip = (page - 1) * 10;
    setSkip(newSkip);
  }, []);

  const itemRender = useCallback((_, type, originalElement) => {
    if (type === "prev") {
      return <ChevronLeft className={styles.pagePrev} />;
    }
    if (type === "next") {
      return <ChevronRight className={styles.pageNext} />;
    }
    return originalElement;
  }, []);

  const renderPaginationToolbar = useCallback(() => {
    if (dataSource.length > perPage) {
      return (
        <div className={styles.paginationView}>
          <Pagination
            current={currentPage}
            total={dataSource.length}
            onChange={handleChangePage}
            className={styles.pagination}
            itemRender={itemRender}
            showSizeChanger={false}
          />
        </div>
      );
    } else {
      return null;
    }
  }, [currentPage, dataSource.length, handleChangePage, itemRender]);

  const tableHeight = useMemo(() => {
    if (dataSource.length > perPage) {
      return 600;
    } else {
      return 64 + 46 * dataSource.length;
    }
  }, [dataSource.length]);

  return (
    <div>
      <div
        id={gradeId}
        className={styles.gradeView}
        onClick={noData ? () => {} : handleSelectGrade}
        style={{
          borderBottom: isLast ? "none" : "",
          cursor: noData ? "default" : "",
        }}
      >
        <div className={styles.gradeLabel}>
          {grade.grade
            ?.replace(" Program", "")
            .replace(/[[\]\\"]/g, "")
            .replace(/,/g, ", ")}
        </div>
        <div className={styles.gradePercent}>
          <div className={styles.flexBox}>
            <div className={styles.progressBackground}>
              <div
                className={styles.progressBar}
                style={{ width: `${grade.progress}%` }}
              >
                {" "}
              </div>
              <div className={styles.completedLessonsTitle}>
                {grade.lessonsCompleted}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.gradeTotal}>
          <div
            className={[
              styles.dropdownToggle,
              selectedId === gradeId
                ? styles.dropdownToggleClose
                : styles.dropdownToggleOpen,
            ].join(" ")}
          >
            <ChevronDown className={styles.icon} />
          </div>
        </div>
      </div>
      <div>
        <Collapse isOpened={isSelected}>
          <div style={{ borderTop: isLast ? "1px solid #E7E7E7" : "" }}>
            <ReactDataGrid
              idProperty="id"
              columns={tableColumns}
              headerHeight={60}
              rowHeight={46}
              dataSource={dataSource || []}
              style={{ minHeight: tableHeight, border: "none" }}
              pagination={true}
              showColumnMenuTool={false}
              limit={perPage}
              skip={skip}
              onSkipChange={setSkip}
              onLimitChange={handleLimitChange}
              enableSelection={false}
              checkboxColumn={false}
              loading={loading}
              loadingText="Loading..."
              renderPaginationToolbar={renderPaginationToolbar}
            />
          </div>
        </Collapse>
      </div>
      {showLesson && showLesson?.lessonId && (
        <LessonProgressModal
          open={showLesson}
          onClose={() => handleShowModal(undefined)}
          lessonId={showLesson?.lessonId}
          fireEvent={false}
        />
      )}
      {showUnit && showUnit?.unitId && (
        <UnitProgressModal
          open={showUnit}
          onClose={() => handleShowModal(undefined)}
          unitId={showUnit?.unitId}
        />
      )}
    </div>
  );
}

export default GradeColumnView;
